import { Suspense, useMemo } from 'react'
import { useGLTF } from '@react-three/drei'
import * as THREE from 'three'
import type { ArenaDef } from './content'
import { AssetBoundary } from './AssetBoundary'
import { scenicAssetFitScale } from './scenicFootprintBudget'
import { SCENARIO_ASSET_URLS } from './generatedArenaScenarios'
import { WORLD_FORGE_COMPOSITIONS, worldForgeCompositionForArena } from './generatedWorldForge'

type ForgeItem = {
  id:string
  layer:'horizon'|'far'|'mid'|'near'|'foreground'
  asset:keyof typeof SCENARIO_ASSET_URLS
  position:readonly [number,number,number]
  rotationY:number
  scale:number
  height:number
  destructible:boolean
  accent:string|null
}

function normalizeWorldForgeAsset(scene:THREE.Object3D,item:ForgeItem){
  const model=scene.clone(true)
  const box=new THREE.Box3().setFromObject(model)
  const size=box.getSize(new THREE.Vector3())
  const targetHeight=Math.max(.2,item.height)
  const scale=scenicAssetFitScale(item.asset,item.layer,targetHeight,size)
  model.scale.setScalar(scale)
  const fitted=new THREE.Box3().setFromObject(model)
  const center=fitted.getCenter(new THREE.Vector3())
  model.position.set(-center.x,-fitted.min.y,-center.z)
  const shadow=item.layer==='near'||item.layer==='foreground'||item.layer==='mid'
  model.traverse(node=>{
    if(!(node instanceof THREE.Mesh))return
    node.castShadow=shadow
    node.receiveShadow=shadow
    if(node.material instanceof THREE.MeshStandardMaterial){
      const m=node.material.clone()
      m.envMapIntensity=item.layer==='horizon'?.72:item.layer==='far'?.92:1.12
      m.roughness=Math.max(.28,Math.min(.86,m.roughness))
      if(item.accent){
        m.emissive=new THREE.Color(item.accent)
        m.emissiveIntensity=.08
      }
      node.material=m
    }
  })
  return model
}

function ForgeAsset({item}:{item:ForgeItem}){
  const {scene}=useGLTF(SCENARIO_ASSET_URLS[item.asset])
  const model=useMemo(()=>normalizeWorldForgeAsset(scene,item),[scene,item])
  return <group
    name={'world-forge:'+item.id}
    position={item.position as [number,number,number]}
    rotation={[0,item.rotationY,0]}
    userData={{worldForge:true,layer:item.layer,destructible:item.destructible}}
  ><primitive object={model}/></group>
}

function ForgeItemView({item}:{item:ForgeItem}){
  return <AssetBoundary fallback={null}><Suspense fallback={null}><ForgeAsset item={item}/></Suspense></AssetBoundary>
}

function WorldForgeSky({arena,composition}:{arena:ArenaDef;composition:ReturnType<typeof worldForgeCompositionForArena>}){
  const celestial=composition.sky.celestial
  return <group name={'world-forge-sky:'+arena.id}>
    <group position={celestial.position as [number,number,number]}>
      <mesh>
        <sphereGeometry args={[celestial.radius,28,18]}/>
        <meshBasicMaterial color={arena.accent} transparent opacity={.11*celestial.intensity} depthWrite={false} blending={THREE.AdditiveBlending}/>
      </mesh>
      <mesh>
        <sphereGeometry args={[celestial.radius*1.34,28,18]}/>
        <meshBasicMaterial color={arena.accent} transparent opacity={.022*celestial.intensity} depthWrite={false} blending={THREE.AdditiveBlending}/>
      </mesh>
    </group>
    {composition.sky.hazePlanes.map((h,index)=><mesh
      key={index}
      position={h.position as [number,number,number]}
      scale={h.scale as [number,number,number]}
    >
      <planeGeometry args={[1,1]}/>
      <meshBasicMaterial color={index===1?arena.accent:arena.fog} transparent opacity={h.opacity} depthWrite={false} blending={THREE.AdditiveBlending} side={THREE.DoubleSide}/>
    </mesh>)}
  </group>
}

export function WorldForgeLayers({arena}:{arena:ArenaDef}){
  if(!(arena.id in WORLD_FORGE_COMPOSITIONS))return null
  const composition=worldForgeCompositionForArena(arena.id)
  const order=['horizon','far','mid','near','foreground'] as const
  return <group name={'world-forge:'+arena.id}>
    <WorldForgeSky arena={arena} composition={composition}/>
    {order.map(layer=><group key={layer} name={'world-forge-layer:'+layer}>
      {composition.layers[layer].map(item=><ForgeItemView key={item.id} item={item as ForgeItem}/>)}
    </group>)}
  </group>
}
